// Canvas 2D renderer. Uses the canvas element's live CSS size as the "design"
// coordinate space — scenes draw in CSS pixels (recomputing layout on resize)
// so there are no letterbox bars. DPR handling keeps the pixel output crisp.

export type ResizeListener = () => void;

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  private cssW = 0;
  private cssH = 0;
  private listeners = new Set<ResizeListener>();

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) throw new Error("2D context not available");
    this.ctx = ctx;
    this.resize();
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    if (w === this.cssW && h === this.cssH) return;
    this.cssW = w;
    this.cssH = h;
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    // Design coords are CSS pixels; dpr only multiplies backing resolution.
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    for (const fn of this.listeners) fn();
  }

  clear(color?: string): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    if (color) {
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    } else {
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
    ctx.restore();
  }

  onResize(fn: ResizeListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  screenToDesign(cssX: number, cssY: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return { x: cssX - rect.left, y: cssY - rect.top };
  }

  get designW(): number { return this.cssW; }
  get designH(): number { return this.cssH; }

  // Kept for backwards compat; no-op now that design follows CSS size.
  setDesignSize(_w: number, _h: number): void {}
}
